import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Check, Download, Lock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [order, setOrder] = useState(null);
  // For guests: an access_token surfaces once the order is fulfilled so we
  // can show/copy the token-scoped access URL right on this page.
  const [accessToken, setAccessToken] = useState(null);
  const { clear } = useCart();
  const { user } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          clear();
          // Signed-in customer → pull from /library.
          // Guest → pull from /payments/order/{sid} which returns access_token.
          try {
            if (user) {
              const lib = await api.get("/library").catch(() => null);
              if (lib?.data) {
                const o = lib.data.orders.find((x) => x.session_id === sessionId);
                if (o) setOrder(o);
              }
            } else {
              const g = await api.get(`/payments/order/${sessionId}`).catch(() => null);
              if (g?.data) {
                setOrder(g.data);
                setAccessToken(g.data.access_token);
              }
            }
          } catch { /* swallow — poll continues */ }
          return;
        }
        if (r.data.payment_status === "expired" || r.data.status === "failed") return;
        attempts.current += 1;
        if (attempts.current < 20) setTimeout(poll, 2000);
      } catch { /* transient — retry */ }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, user]);

  const guestAccessUrl = accessToken ? `${window.location.origin}/access/${accessToken}` : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="success-page">
      {status.payment_status !== "paid" ? (
        <div>
          <div className="eyebrow mb-4">Confirming your order…</div>
          <div className="font-serif text-3xl text-navy">Processing payment</div>
          <p className="text-navy/60 mt-6">This usually takes a few seconds. Please don&apos;t close this page.</p>
        </div>
      ) : (
        <div data-testid="success-confirmed">
          <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center mb-8">
            <Check className="w-8 h-8 text-gold" strokeWidth={2} />
          </div>
          <div className="eyebrow mb-3">Confirmed</div>
          <h1 className="aperture-h1 text-4xl md:text-5xl">Thank you.</h1>
          <p className="mt-6 text-navy/70 max-w-lg mx-auto leading-relaxed">
            Your order is confirmed. A receipt has been sent to your email.
            {guestAccessUrl && " We&apos;ve also emailed you a secure link to your materials — you can open them right now below."}
          </p>

          {/* Guest access panel — shows the tokenized link inline so buyers
              don't need to check email to grab their downloads. */}
          {guestAccessUrl && (
            <div className="mt-10 text-left bg-white shadow-xl rounded-sm p-8 border" style={{ borderColor: "rgba(10,37,64,0.08)" }}
                 data-testid="guest-access-panel">
              <div className="eyebrow mb-3 flex items-center gap-2"><Lock className="w-3 h-3" /> Your secure access link</div>
              <div className="font-serif text-lg text-navy mb-4">Open your library — no account required.</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/access/${accessToken}`} data-testid="success-open-access-btn"
                      className="btn-primary flex-1 px-6 py-3 text-xs uppercase tracking-widest rounded-sm inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Open my materials
                </Link>
                <button data-testid="success-copy-access-link"
                        onClick={() => { navigator.clipboard?.writeText(guestAccessUrl); }}
                        className="px-4 py-3 border rounded-sm text-xs uppercase tracking-widest text-navy hover:border-gold hover:text-gold transition-colors"
                        style={{ borderColor: "rgba(10,37,64,0.15)" }}>
                  Copy link
                </button>
              </div>
              <div className="mt-4 text-[11px] text-navy/50 break-all font-mono" data-testid="success-access-url">{guestAccessUrl}</div>
              <div className="mt-4 text-xs text-navy/60 leading-relaxed">
                Save this email or bookmark the link — it&apos;s the only key to your order.
                It only unlocks the items in this purchase and never expires.
              </div>
            </div>
          )}

          {/* Signed-in path retains the direct-download buttons (unchanged). */}
          {!accessToken && order?.items?.map((i) => {
            const t = (() => { try { return localStorage.getItem("aperture_token") || ""; } catch { return ""; } })();
            const href = `${API}/download/${sessionId}/${i.ebook_id}${t ? `?token=${encodeURIComponent(t)}` : ""}`;
            return (
              <a key={i.ebook_id} href={href} data-testid={`success-download-${i.ebook_id}`}
                 className="mt-6 inline-flex items-center gap-3 btn-primary px-8 py-4 text-xs uppercase tracking-widest rounded-sm mr-3">
                <Download className="w-4 h-4" /> Download {i.title}
              </a>
            );
          })}
          <div className="mt-10">
            <Link to={user ? "/library" : "/catalog"} data-testid="success-library-btn" className="text-sm text-navy hover:text-gold transition-colors">
              {user ? "Go to My Library →" : "Browse more titles →"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
